'use client';

/**
 * HeroAnimated — Hero da home / (Figma 81:70 §Hero).
 *
 * Comportamento (per spec PL 2026-05-15 — reforma 2):
 *   - Ocupa 90vh
 *   - Animação supercut.ai style: mídias SUMEM (fade-out) e APARECEM noutra
 *     posição (fade-in), NÃO transitam continuamente.
 *   - Sem sombra, sem rotação, radius 4px
 *   - 5s entre flips, 0.6s fade
 *   - CTA "Fazer parte" (primary-large) rola até #waitlist
 *
 * Mídias: 7 vídeos curtos (muted/loop) distribuídos pelos 3 slots,
 * variando posições (presets) a cada flip.
 */
import * as React from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { Button } from '@brisa/ui/components/ui/button';

export interface HeroAnimatedProps {
  headline: string;
  subhead: string;
  ctaLabel: string;
  scrollTarget?: string;
  surface?: string;
}

interface SlotPreset {
  top: string;
  left?: string;
  right?: string;
  /** Largura CSS clamp (responsivo mobile→desktop). */
  width: string;
}

interface Slot {
  id: string;
  /** Lista de mídias (urls). Cycla na ordem. */
  media: string[];
  /** Lista de presets de posição. Cycla em paralelo. */
  presets: SlotPreset[];
  /** Atraso inicial (segundos) — distribui flips no tempo. */
  startDelay: number;
}

const SLOTS: Slot[] = [
  {
    id: 'slot-a',
    media: [
      '/landing/hero/video01.mp4',
      '/landing/hero/video04.mp4',
      '/landing/hero/video07.mp4',
    ],
    presets: [
      { top: '20%', left: '8%', width: 'clamp(120px,17vw,240px)' },
      { top: '52%', left: '14%', width: 'clamp(100px,15vw,210px)' },
      { top: '14%', left: '18%', width: 'clamp(110px,16vw,220px)' },
    ],
    startDelay: 0,
  },
  {
    id: 'slot-b',
    media: [
      '/landing/hero/video02.mp4',
      '/landing/hero/video05.mp4',
    ],
    presets: [
      { top: '12%', right: '10%', width: 'clamp(100px,15vw,210px)' },
      { top: '50%', right: '6%', width: 'clamp(90px,13vw,180px)' },
      { top: '22%', right: '14%', width: 'clamp(95px,14vw,200px)' },
    ],
    startDelay: 1.6,
  },
  {
    id: 'slot-c',
    media: [
      '/landing/hero/video03.mp4',
      '/landing/hero/video06.mp4',
    ],
    presets: [
      { top: '68%', left: '46%', width: 'clamp(0px,12vw,160px)' },
      { top: '8%', left: '40%', width: 'clamp(0px,11vw,150px)' },
    ],
    startDelay: 3.2,
  },
];

const FLIP_INTERVAL_MS = 5000;
const FADE_DURATION_S = 0.6;

function HeroSlot({ slot }: { slot: Slot }) {
  const [index, setIndex] = React.useState(0);
  const prefersReducedMotion = useReducedMotion();

  React.useEffect(() => {
    if (prefersReducedMotion) return;
    let intervalId: number | null = null;
    const cycleLen = Math.max(slot.presets.length, slot.media.length);
    const timeoutId = window.setTimeout(() => {
      setIndex((i) => (i + 1) % cycleLen);
      intervalId = window.setInterval(() => {
        setIndex((i) => (i + 1) % cycleLen);
      }, FLIP_INTERVAL_MS);
    }, slot.startDelay * 1000);
    return () => {
      window.clearTimeout(timeoutId);
      if (intervalId != null) window.clearInterval(intervalId);
    };
  }, [prefersReducedMotion, slot.presets.length, slot.media.length, slot.startDelay]);

  const preset = slot.presets[index % slot.presets.length]!;
  const mediaUrl = slot.media[index % slot.media.length]!;
  const isVideo = /\.(mp4|webm|mov)(\?|$)/i.test(mediaUrl);

  // Cada combinação preset+media gera uma key única — AnimatePresence
  // dispara fade-out completo do anterior + fade-in do novo na nova posição.
  const visualKey = `${slot.id}-${index}-${mediaUrl}`;

  return (
    <AnimatePresence>
      <motion.div
        key={visualKey}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: FADE_DURATION_S, ease: 'easeInOut' }}
        className="pointer-events-none absolute overflow-hidden rounded-[4px]"
        style={{
          top: preset.top,
          left: preset.left ?? 'auto',
          right: preset.right ?? 'auto',
          width: preset.width,
          aspectRatio: '4 / 5',
        }}
        aria-hidden="true"
        data-slot={`hero-${slot.id}`}
      >
        {isVideo ? (
          <video
            src={mediaUrl}
            autoPlay
            muted
            loop
            playsInline
            className="h-full w-full object-cover"
          />
        ) : (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={mediaUrl}
            alt=""
            className="h-full w-full object-cover"
            loading="eager"
          />
        )}
      </motion.div>
    </AnimatePresence>
  );
}

export function HeroAnimated({
  headline,
  subhead,
  ctaLabel,
  scrollTarget = 'waitlist',
  surface = 'home_hero',
}: HeroAnimatedProps) {
  const handleCtaClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const el = document.getElementById(scrollTarget);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <section
      className="relative flex w-full items-center justify-center overflow-hidden"
      style={{ minHeight: '80vh', maxHeight: '90vh' }}
      aria-labelledby="hero-headline"
    >
      {/* Slots de mídia (fade-out → fade-in noutra posição) */}
      {SLOTS.map((slot) => (
        <HeroSlot key={slot.id} slot={slot} />
      ))}

      {/* Conteúdo central — escala com vh pra sempre caber em 90vh (notebook → 4K) */}
      <div className="relative z-10 mx-auto flex w-full max-w-[1440px] flex-col items-center px-6 pt-[72px] text-center sm:px-10 md:pt-[104px] lg:px-20">
        <motion.h1
          id="hero-headline"
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: 'easeOut' as const, delay: 0.1 }}
          className="font-display font-light leading-[0.92] tracking-[-0.02em] text-[hsl(var(--accent-yellow))]"
          style={{
            // min(...) garante que tanto 12vw quanto 18vh limitam a fonte —
            // em laptops 13"+ (vh menor) o headline diminui pra caber subhead+CTA.
            fontSize: 'clamp(40px, min(12vw, 18vh), 160px)',
          }}
        >
          {headline}
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: 'easeOut', delay: 0.7 }}
          className="mt-[clamp(12px,3vh,32px)] max-w-[420px] font-body text-lg font-medium leading-snug text-[hsl(var(--accent-yellow))] sm:text-base"
        >
          {subhead}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: 'easeOut', delay: 1.0 }}
          className="mt-[clamp(16px,4vh,40px)]"
        >
          <Button
            type="button"
            onClick={handleCtaClick}
            variant="primary-large"
            size="lg"
            data-event="landing-cta"
            data-surface={surface}
            data-destination="waitlist"
          >
            {ctaLabel}
          </Button>
        </motion.div>
      </div>
    </section>
  );
}
